import React from "react";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { listJobs } from "../../api/jobs";
import {
  calculatePackoutEstimate,
  createPersonalPropertyAssessment,
  downloadContentsCornerCsv,
  downloadPackoutEstimateCsv,
  downloadPackoutEstimatePdf,
  getPersonalPropertyAssessment,
  runPersonalPropertyAssessment,
  uploadPersonalPropertySources,
} from "../../api/personalProperty";

export default function PersonalPropertyWorkspace() {
  const [jobs, setJobs] = React.useState([]);
  const [jobId, setJobId] = React.useState("");
  const [manualJobId, setManualJobId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [assessmentId, setAssessmentId] = React.useState("");
  const [files, setFiles] = React.useState([]);
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [busyCreate, setBusyCreate] = React.useState(false);
  const [busyUpload, setBusyUpload] = React.useState(false);
  const [busyRun, setBusyRun] = React.useState(false);
  const [busyExport, setBusyExport] = React.useState(false);
  const [busyPackout, setBusyPackout] = React.useState(false);
  const [busyPackoutCsv, setBusyPackoutCsv] = React.useState(false);
  const [busyPackoutPdf, setBusyPackoutPdf] = React.useState(false);
  const [packoutResult, setPackoutResult] = React.useState(null);
  const [packoutForm, setPackoutForm] = React.useState({
    affected_rooms: "6",
    packout_level: "standard",
    storage_months: "1",
    distance_miles: "15",
    contents_density: "1",
    labor_rate: "55",
    helper_rate: "35",
    truck_rate_per_trip: "185",
    storage_vault_rate_monthly: "95",
    material_markup: "0.15",
  });

  const selectedJobId = String(manualJobId || jobId || "").trim();

  const refreshJobs = React.useCallback(async () => {
    try {
      const rows = await listJobs({ limit: 200, offset: 0 });
      setJobs(Array.isArray(rows) ? rows : []);
      if (!jobId && Array.isArray(rows) && rows.length > 0) {
        setJobId(String(rows[0].id || ""));
      }
    } catch (_e) {
      setJobs([]);
    }
  }, [jobId]);

  React.useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  const onCreate = async () => {
    setError("");
    setBusyCreate(true);
    try {
      if (!selectedJobId) throw new Error("Select or enter a job id first.");
      const created = await createPersonalPropertyAssessment({ job_id: selectedJobId, notes });
      const id = String(created?.id || "");
      if (!id) throw new Error("No assessment id returned.");
      setAssessmentId(id);
      const loaded = await getPersonalPropertyAssessment(id);
      setPayload(loaded);
    } catch (e) {
      setError(e?.message || "Failed to create assessment");
    } finally {
      setBusyCreate(false);
    }
  };

  const onUpload = async () => {
    setError("");
    setBusyUpload(true);
    try {
      if (!assessmentId) throw new Error("Create assessment first.");
      if (!files.length) throw new Error("Select one or more files.");
      await uploadPersonalPropertySources(assessmentId, files);
      setFiles([]);
      const loaded = await getPersonalPropertyAssessment(assessmentId);
      setPayload(loaded);
    } catch (e) {
      setError(e?.message || "Upload failed");
    } finally {
      setBusyUpload(false);
    }
  };

  const onRun = async () => {
    setError("");
    setBusyRun(true);
    try {
      if (!assessmentId) throw new Error("Create assessment first.");
      const result = await runPersonalPropertyAssessment(assessmentId, { maxItems: 100, priceLookup: true });
      setPayload(result);
    } catch (e) {
      setError(e?.message || "Run failed");
    } finally {
      setBusyRun(false);
    }
  };

  const onExportCsv = async () => {
    setError("");
    setBusyExport(true);
    try {
      if (!assessmentId) throw new Error("Create assessment first.");
      const { blob, filename } = await downloadContentsCornerCsv(assessmentId);
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) {
      setError(e?.message || "CSV export failed");
    } finally {
      setBusyExport(false);
    }
  };

  const buildPackoutPayload = () => ({
    estimate_name: `packout_${String(selectedJobId || "estimate").trim() || "estimate"}`,
    affected_rooms: Number(packoutForm.affected_rooms || 0),
    packout_level: String(packoutForm.packout_level || "standard"),
    storage_months: Number(packoutForm.storage_months || 0),
    distance_miles: Number(packoutForm.distance_miles || 0),
    contents_density: Number(packoutForm.contents_density || 1),
    labor_rate: Number(packoutForm.labor_rate || 0),
    helper_rate: Number(packoutForm.helper_rate || 0),
    truck_rate_per_trip: Number(packoutForm.truck_rate_per_trip || 0),
    storage_vault_rate_monthly: Number(packoutForm.storage_vault_rate_monthly || 0),
    material_markup: Number(packoutForm.material_markup || 0),
  });

  const onCalculatePackout = async () => {
    setError("");
    setBusyPackout(true);
    try {
      const result = await calculatePackoutEstimate(buildPackoutPayload());
      setPackoutResult(result);
    } catch (e) {
      setError(e?.message || "Packout calculation failed");
    } finally {
      setBusyPackout(false);
    }
  };

  const onExportPackoutCsv = async () => {
    setError("");
    setBusyPackoutCsv(true);
    try {
      const { blob, filename } = await downloadPackoutEstimateCsv(buildPackoutPayload());
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) {
      setError(e?.message || "Packout CSV export failed");
    } finally {
      setBusyPackoutCsv(false);
    }
  };

  const onExportPackoutPdf = async () => {
    setError("");
    setBusyPackoutPdf(true);
    try {
      const { blob, filename } = await downloadPackoutEstimatePdf(buildPackoutPayload());
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) {
      setError(e?.message || "Packout PDF export failed");
    } finally {
      setBusyPackoutPdf(false);
    }
  };

  const items = payload?.items || [];
  const sources = payload?.sources || [];
  const packoutMaterials = packoutResult?.materials || [];
  const packoutLines = packoutResult?.estimate_lines || [];
  const packoutTotals = packoutResult?.totals || {};

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-2xl font-semibold text-slate-900">Contents Corner</h1>
          <p className="mt-1 text-sm text-slate-500">
            Upload handwritten notes, spreadsheets, PDFs, or photos to build a property inventory with replacement pricing.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-700">
              Job
              <select
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
              >
                <option value="">Select job</option>
                {jobs.map((j) => (
                  <option key={String(j?.id || "")} value={String(j?.id || "")}>
                    {String(j?.name || "(untitled)")} ({String(j?.id || "")})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-700">
              Or manual job id
              <Input className="mt-1" value={manualJobId} onChange={(e) => setManualJobId(e.target.value)} />
            </label>
          </div>

          <label className="block text-sm text-slate-700">
            Notes
            <textarea
              className="mt-1 min-h-[80px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <Button onClick={onCreate} disabled={busyCreate}>{busyCreate ? "Creating..." : "Create Assessment"}</Button>
            <Button variant="secondary" onClick={onRun} disabled={busyRun || !assessmentId}>
              {busyRun ? "Running..." : "Analyze + Price"}
            </Button>
            <Button variant="ghost" onClick={onExportCsv} disabled={busyExport || !assessmentId || items.length < 1}>
              {busyExport ? "Exporting..." : "Export Xactimate Contents CSV"}
            </Button>
          </div>
          {assessmentId ? <p className="text-xs font-mono text-slate-600">Assessment: {assessmentId}</p> : null}

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium text-slate-800">Upload Sources</p>
            <input
              className="mt-2 block w-full text-sm"
              type="file"
              multiple
              accept=".txt,.md,.csv,.tsv,.xlsx,.pdf,image/*"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            <div className="mt-2">
              <Button size="sm" onClick={onUpload} disabled={busyUpload || !assessmentId}>
                {busyUpload ? "Uploading..." : "Upload Files"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-600">Uploaded sources: {sources.length}</p>
          </div>

          {items.length > 0 ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-600">Item</th>
                    <th className="px-3 py-2 text-left text-slate-600">Qty</th>
                    <th className="px-3 py-2 text-left text-slate-600">Low Replacement</th>
                    <th className="px-3 py-2 text-left text-slate-600">Quotes</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={String(it?.id || "")} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2 text-slate-900">{String(it?.name || "")}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {String(it?.quantity || "")} {String(it?.quantity_unit || "")}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {it?.replacement_price_low != null ? `$${Number(it.replacement_price_low).toFixed(2)}` : "n/a"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700">
                        <ul className="space-y-1">
                          {(it?.quotes || []).slice(0, 3).map((q, idx) => (
                            <li key={`${it?.id || "item"}-${idx}`}>
                              {q?.vendor || "vendor"}: {q?.price != null ? `$${Number(q.price).toFixed(2)}` : "n/a"}{" "}
                              {q?.url ? <a className="text-blue-700 underline" href={q.url} target="_blank" rel="noreferrer">link</a> : null}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-slate-900">Contents Packout Calculator</h2>
          <p className="mt-1 text-sm text-slate-500">
            Calculates restoration packout materials and estimate totals using industry-standard assumptions that you can override.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm text-slate-700">
              Affected rooms
              <Input className="mt-1" type="number" min="1" value={packoutForm.affected_rooms} onChange={(e) => setPackoutForm((p) => ({ ...p, affected_rooms: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Packout level
              <select
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                value={packoutForm.packout_level}
                onChange={(e) => setPackoutForm((p) => ({ ...p, packout_level: e.target.value }))}
              >
                <option value="light">Light</option>
                <option value="standard">Standard</option>
                <option value="heavy">Heavy</option>
              </select>
            </label>
            <label className="text-sm text-slate-700">
              Storage months
              <Input className="mt-1" type="number" min="0" step="0.1" value={packoutForm.storage_months} onChange={(e) => setPackoutForm((p) => ({ ...p, storage_months: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Distance (miles)
              <Input className="mt-1" type="number" min="0" step="1" value={packoutForm.distance_miles} onChange={(e) => setPackoutForm((p) => ({ ...p, distance_miles: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Contents density
              <Input className="mt-1" type="number" min="0.5" max="2" step="0.1" value={packoutForm.contents_density} onChange={(e) => setPackoutForm((p) => ({ ...p, contents_density: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Labor rate ($/hr)
              <Input className="mt-1" type="number" min="0" step="0.01" value={packoutForm.labor_rate} onChange={(e) => setPackoutForm((p) => ({ ...p, labor_rate: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Helper rate ($/hr)
              <Input className="mt-1" type="number" min="0" step="0.01" value={packoutForm.helper_rate} onChange={(e) => setPackoutForm((p) => ({ ...p, helper_rate: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Truck rate ($/trip)
              <Input className="mt-1" type="number" min="0" step="0.01" value={packoutForm.truck_rate_per_trip} onChange={(e) => setPackoutForm((p) => ({ ...p, truck_rate_per_trip: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Vault rate ($/month)
              <Input className="mt-1" type="number" min="0" step="0.01" value={packoutForm.storage_vault_rate_monthly} onChange={(e) => setPackoutForm((p) => ({ ...p, storage_vault_rate_monthly: e.target.value }))} />
            </label>
            <label className="text-sm text-slate-700">
              Material markup (0-1)
              <Input className="mt-1" type="number" min="0" max="1" step="0.01" value={packoutForm.material_markup} onChange={(e) => setPackoutForm((p) => ({ ...p, material_markup: e.target.value }))} />
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={onCalculatePackout} disabled={busyPackout}>
              {busyPackout ? "Calculating..." : "Calculate Packout Estimate"}
            </Button>
            <Button variant="secondary" onClick={onExportPackoutCsv} disabled={busyPackoutCsv}>
              {busyPackoutCsv ? "Exporting..." : "Export Packout CSV"}
            </Button>
            <Button variant="secondary" onClick={onExportPackoutPdf} disabled={busyPackoutPdf}>
              {busyPackoutPdf ? "Exporting..." : "Export Packout PDF"}
            </Button>
          </div>

          {packoutResult ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                <p>Basis: {String(packoutResult?.assumptions?.standard_basis || "")}</p>
                <p>Effective multiplier: {Number(packoutResult?.assumptions?.effective_multiplier || 1).toFixed(2)}</p>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-600">Material</th>
                      <th className="px-3 py-2 text-left text-slate-600">Qty</th>
                      <th className="px-3 py-2 text-left text-slate-600">Unit Cost</th>
                      <th className="px-3 py-2 text-left text-slate-600">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packoutMaterials.map((m, idx) => (
                      <tr key={`mat-${idx}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-900">{String(m?.name || "")}</td>
                        <td className="px-3 py-2 text-slate-700">{String(m?.quantity ?? "")}</td>
                        <td className="px-3 py-2 text-slate-700">${Number(m?.unit_cost || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-slate-700">${Number(m?.line_total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-600">Estimate Line</th>
                      <th className="px-3 py-2 text-left text-slate-600">Qty</th>
                      <th className="px-3 py-2 text-left text-slate-600">Unit</th>
                      <th className="px-3 py-2 text-left text-slate-600">Unit Cost</th>
                      <th className="px-3 py-2 text-left text-slate-600">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packoutLines.map((line, idx) => (
                      <tr key={`line-${idx}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-900">{String(line?.name || "")}</td>
                        <td className="px-3 py-2 text-slate-700">{String(line?.quantity ?? "")}</td>
                        <td className="px-3 py-2 text-slate-700">{String(line?.unit || "")}</td>
                        <td className="px-3 py-2 text-slate-700">${Number(line?.unit_cost || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-slate-700">${Number(line?.line_total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                <p>Materials Subtotal: ${Number(packoutTotals?.materials_subtotal || 0).toFixed(2)}</p>
                <p>Labor Subtotal: ${Number(packoutTotals?.labor_subtotal || 0).toFixed(2)}</p>
                <p>Transport Subtotal: ${Number(packoutTotals?.transport_subtotal || 0).toFixed(2)}</p>
                <p>Storage Subtotal: ${Number(packoutTotals?.storage_subtotal || 0).toFixed(2)}</p>
                <p className="font-semibold">Packout Estimate Total: ${Number(packoutTotals?.grand_total || 0).toFixed(2)}</p>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
